import { useAuth } from '@agentnet/auth';
import { useFeatureFlag } from '@agentnet/feature-flags';
import { HashUtils } from '@agentnet/utils';
import DateFnsUtils from '@date-io/date-fns';
import { AgentNetButton, Content, PageHeader } from '@fluentsms/agentnet-web-components';
import { FormControlLabel, Grid, Switch, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import HistoryOutlinedIcon from '@material-ui/icons/HistoryOutlined';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { getFileInfo, saveFileReopen, saveFileStatus } from 'api/file/file-api';
import { fileInfoType } from 'api/file/interfaces/get-file';
import {
  createCreditMemoApi,
  createInvoicedFileApi,
  firmSearchApi,
  getAccountInfoApi,
  getCplOnlyRemittedFileEligibilityApi,
  getInvoiceDetailsApi,
  getInvoicesApi,
} from 'api/orderManagement/order-management-api';
import { format as formatDate, parse } from 'date-fns';
import { AGENCY_SUPPORT_MESSAGE, fileStatuses, KBLinks } from 'features/constants';
import { InvoiceItems, InvoiceModel, OrdersDetailsModel } from 'features/Remittance/types';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import useSessionStorage from 'hooks/useSessionStorage';
import { useContext, useEffect, useMemo, useState } from 'react';
import AgentNetButton2 from 'ui-kit/components/button/AgentNetButton';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import AgentNetConfirmFileReopenModal from 'ui-kit/components/modal/AgentNetConfirmFileReopenModal';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import InvoiceSummaryTable from 'ui-kit/components/payment/InvoiceSummaryTable';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import { CalendarIcon } from 'ui-kit/icons/OrderManagement';
import { AgentNetTextInput } from 'ui-kit/inputs';
import AgentNetDropdownSelector from 'ui-kit/inputs/AgentNetDropdownSelector';
import AutocompleteFirmSearch from 'ui-kit/inputs/AutocompleteFirmSearch/AutocompleteFirmSearch';
import DateFieldString from 'ui-kit/inputs/DateField/DateFieldString';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { getDateRangeFromPeriod } from 'utilities/utilities';
import { dateFilters } from './Dashboard';
import InvoicesGrid from './InvoicesGrid';
import {
  getReopenButtonTooltip,
  shouldApplyDateFilter,
  shouldDisableReopenButton,
  validateFileNumber,
} from './invoice-utilities';
import {
  CreateInvoicedFileRequestModel,
  CreateInvoicedFileResponseModel,
  FirmListType,
  InvoiceSearchRequestModel,
  InvoiceSearchResult,
} from './types';

const dataQaPrefix = 'OrderManagement-Invoices';

interface ValidationErrors {
  firm?: string;
}

export interface AdvancedSearchTextFieldsProps {
  invoiceNumber: string;
  onClearSearch: () => void;
  onSearch: (params: {
    firmId?: number;
    policyNumber: string;
    fileNumber: string;
    orderNumber?: number;
    applyDateFilter: boolean;
  }) => void;
}

interface InvoicesPageProps {
  pageHeader?: string;
  useHeaderDivider?: boolean;
  showHeaderElipsis?: boolean;
}

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      '& .ag-center-cols-viewport': {
        minHeight: 220,
      },
    },
    noRows: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 220,
      justifyContent: 'center',
    },
    reopenFile: {
      background: theme.palette.warning.main,
      marginLeft: theme.spacing(1),
      '&:hover &:focus': {
        backgroundColor: theme.palette.warning.dark,
      },
    },
    errorText: {
      color: theme.palette.error.main,
      fontSize: '0.75rem',
      marginTop: 4,
    },
  }),
);

const AdvancedSearchTextFields = ({ invoiceNumber, onClearSearch, onSearch }: AdvancedSearchTextFieldsProps) => {
  const classes = useStyles();
  const { getAccessToken } = useAuth();
  const [policyNumber, setPolicyNumber] = useState<string>('');
  const [fileNumber, setFileNumber] = useState<string>('');
  const [orderNumber, setOrderNumber] = useState<number | undefined>(undefined);
  const [firmList, setFirmList] = useState<Array<FirmListType>>([]);
  const [firmSearchInput, setFirmSearchInput] = useState<string>('');
  const [myFirm, setMyFirm] = useState<FirmListType | undefined>(undefined);
  const [noMatchesLabel, setNoMatchesLabel] = useState<string>('Enter at least 3 characters to refine search');
  const defaultNoMatchesMessage = 'Enter at least 3 characters to refine search';
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const firmSearch = async (input: string): Promise<Array<FirmListType>> => {
    const token = await getAccessToken();
    const response = await firmSearchApi({ searchText: input }, token);
    const firmList: Array<FirmListType> = [];
    if (Array.isArray(response.result)) {
      response.result.forEach((firm) => {
        firmList.push({
          name: firm.firmName,
          value: firm.firmId,
          display: firm.firmName + ' (' + firm.firmId + ')',
          isActive: firm.isActive,
        });
      });
    }

    if (firmList.length === 1 && input.length > 5 && !isNaN(Number(input))) {
      firmList[0].accountNumber = input;
    } else if (firmList.length === 0 && input.length > 2 && !isNaN(Number(input))) {
      setNoMatchesLabel('Enter complete account number to refine search');
    } else if (!firmList.length) {
      setNoMatchesLabel('No matching firms found');
    } else {
      setNoMatchesLabel(defaultNoMatchesMessage);
    }
    return firmList;
  };

  useEffect(() => {
    const searchFirmsTimeout = setTimeout(async () => {
      if (firmSearchInput?.length > 2) {
        const result = await firmSearch(firmSearchInput);
        setFirmList(result);
      } else {
        setNoMatchesLabel(defaultNoMatchesMessage);
      }
    }, 500);

    return () => clearTimeout(searchFirmsTimeout);
  }, [firmSearchInput]);

  const getSelectedFirmOption = (event: number) => {
    if (!event) {
      setMyFirm(undefined);
      return;
    }
    if (firmList.length > 0) {
      const selectedFirm = firmList.find((firm) => firm.value === event);
      if (selectedFirm) {
        setMyFirm(selectedFirm);
      }
    }
  };

  const clearFirmSearch = () => {
    setFirmList([]);
    setFirmSearchInput('');
    setMyFirm(undefined);
    setNoMatchesLabel(defaultNoMatchesMessage);
  };

  const validateSearch = (): boolean => {
    const errors: ValidationErrors = {};
    let isValid = true;

    const hasNonInvoiceSearchField = Boolean(policyNumber || orderNumber || fileNumber);
    const hasFirm = Boolean(myFirm?.value);
    const hasInvoiceNumber = Boolean(invoiceNumber);
    const hasAnySearchField = hasInvoiceNumber || hasNonInvoiceSearchField || hasFirm;

    // Rule 1: At least one search field is required - if none provided, firm is required
    if (!hasAnySearchField) {
      errors.firm = 'Firm is required when no other search fields are provided';
      isValid = false;
    }
    // Rule 2: Policy number, order number, or file number require firm
    else if (hasNonInvoiceSearchField && !hasFirm) {
      errors.firm = 'Firm is required when searching by policy number, order number, or file number';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const validateAndSearch = () => {
    // Clear previous errors
    setValidationErrors({});

    // Validate
    if (!validateSearch()) {
      return;
    }

    // Determine if date filter should be applied
    // Date filters only apply when searching by firm alone (no invoice, policy, order, or file number)
    const applyDateFilter = shouldApplyDateFilter({
      hasInvoiceNumber: Boolean(invoiceNumber),
      hasPolicyNumber: Boolean(policyNumber),
      hasOrderNumber: Boolean(orderNumber),
      hasFileNumber: Boolean(fileNumber),
      hasFirm: Boolean(myFirm?.value),
    });

    onSearch({
      firmId: myFirm?.value,
      policyNumber,
      fileNumber,
      orderNumber,
      applyDateFilter,
    });
  };

  // Ensure selected firm is in the options list
  if (!firmList?.length && myFirm) {
    firmList.push(myFirm);
  }

  return (
    <>
      <Grid item xs={6} sm={3}>
        <AgentNetTextInput
          fullWidth
          data-testid={`${dataQaPrefix}-Advanced-Search-PolicyNumber`}
          label={'Policy Number'}
          value={policyNumber}
          onChange={(event) => {
            const value = event.target.value?.trim() ?? '';
            setPolicyNumber(value);
          }}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <AgentNetTextInput
          fullWidth
          type="number"
          data-testid={`${dataQaPrefix}-Advanced-Search-OrderNumber`}
          label={'Order Number'}
          value={!Number.isNaN(orderNumber) ? String(orderNumber || '') : ''}
          onChange={(event) => {
            const value = event.target.value;
            const numValue = value === '' ? undefined : Number(value);
            setOrderNumber(numValue);
          }}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <AgentNetTextInput
          fullWidth
          data-testid={`${dataQaPrefix}-Advanced-Search-FileNumber`}
          label={'File Number'}
          value={fileNumber}
          onChange={(event) => {
            const value = event.target.value?.trim() ?? '';
            setFileNumber(value);
          }}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <AutocompleteFirmSearch
          label="Firm Name"
          name="firmSearchInput"
          data-testid={`${dataQaPrefix}-Advanced-Search-FirmName`}
          hideSearchIcon
          value={firmSearchInput ? myFirm?.value : -1}
          onChange={(eventOrValue) => {
            // Handle both text input events and firm selection values
            if (eventOrValue && typeof eventOrValue === 'object' && 'target' in eventOrValue) {
              // Text input event - update search text
              setMyFirm(undefined);
              setFirmSearchInput((eventOrValue.target as HTMLInputElement)?.value);
              // Clear validation error when user types
              if (validationErrors.firm) {
                setValidationErrors({});
              }
            } else if (typeof eventOrValue === 'number') {
              // Firm selection from dropdown
              getSelectedFirmOption(eventOrValue);
              // Clear validation error when firm is selected
              if (validationErrors.firm) {
                setValidationErrors({});
              }
            } else if (typeof eventOrValue === 'string') {
              // Clear firm
              getSelectedFirmOption(0);
            }
          }}
          options={firmList}
          noMatchesLabel={noMatchesLabel}
        />
        {validationErrors.firm && (
          <Typography className={classes.errorText} data-testid={`${dataQaPrefix}-FirmError`}>
            {validationErrors.firm}
          </Typography>
        )}
      </Grid>
      <Grid item xs={2} sm={1}>
        <AgentNetButton
          color="primary"
          data-testid="ClearSearch"
          onClick={() => {
            clearFirmSearch();
            setPolicyNumber('');
            setFileNumber('');
            setOrderNumber(undefined);
            onClearSearch();
          }}
          variant="outlined"
          id="clear-search"
          size="small"
        >
          Clear Search
        </AgentNetButton>
      </Grid>
      <Grid item xs={2} sm={1}>
        <AgentNetButton
          color="primary"
          data-testid="Search"
          variant="contained"
          onClick={validateAndSearch}
          id="search"
          size="small"
        >
          Search
        </AgentNetButton>
      </Grid>
    </>
  );
};

const InvoicesPage = ({ pageHeader, useHeaderDivider, showHeaderElipsis }: InvoicesPageProps) => {
  const classes = useStyles();
  const { getAccessToken } = useAuth();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm } = profileCtx;
  const includeAgentNetData = useFeatureFlag(FeatureFlag.INCLUDE_AGENT_NET_DATA_INVOICING, false);

  const [dateFilter, setDateFilter] = useState<string>('today');
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [advancedSearch, setAdvancedSearch] = useSessionStorage('useInvoicesAdvancedSearch');
  const [searchExecuted, setSearchExecuted] = useState<boolean>(false);
  const [selectedInvoices, setSelectedInvoices] = useState<InvoiceSearchResult[]>([]);
  const [invoiceForDrawer, setInvoiceForDrawer] = useState<InvoiceSearchResult | undefined>(undefined);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [invoiceDetailsData, setInvoiceDetailsData] = useState<OrdersDetailsModel | undefined>(undefined);
  const [invoiceFileData, setInvoiceFileData] = useState<fileInfoType | undefined>(undefined);
  const [invoiceDetailsLoading, setInvoiceDetailsLoading] = useState<boolean>(false);
  const [fileReopenModal, setFileReopenModal] = useState<boolean>(false);
  const [isFileReopenInProgress, setFileReopenInProgress] = useState<boolean>(false);
  const [isCreatingFile, setIsCreatingFile] = useState<boolean>(false);
  const [isCheckingFileExistence, setIsCheckingFileExistence] = useState<boolean>(false);
  const [isCplOnlyReopenEligible, setIsCplOnlyReopenEligible] = useState<boolean>(false);
  const [isCheckingCplEligibility, setIsCheckingCplEligibility] = useState<boolean>(false);
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [orderDate, setOrderDate] = useState<string>('');
  const [matchedInvoice, setMatchedInvoice] = useState<InvoiceModel | undefined>(undefined);
  const { addGlobalMsg } = useGlobalMessages();
  const { addSnackbarMessage } = useSnackBars();
  const { profile } = profileCtx;

  const hasRemitAccess = useMemo(() => {
    return Boolean(profile?.activityRights?.find((right) => right.ActivityRightId === 25));
  }, [profile?.activityRights]);

  useEffect(() => {
    // Default advanced search to true if not set
    if (advancedSearch === null) {
      setAdvancedSearch(true);
    }
  }, []);

  useEffect(() => {
    // Update date range based on selected filter
    if (dateFilter === 'custom') {
      // Don't auto-update dates for custom selection
      return;
    }
    const { from, to } = getDateRangeFromPeriod(dateFilter);
    setStartDate(from);
    setEndDate(to);
  }, [dateFilter]);

  const searchInvoices = async (params: {
    firmId?: number;
    policyNumber: string;
    fileNumber: string;
    orderNumber?: number;
    applyDateFilter: boolean;
  }): Promise<InvoiceSearchResult[]> => {
    const token = await getAccessToken();

    // Base payload common to both requests
    // Only include dates when applyDateFilter is true (searching by firm alone)
    const basePayload = {
      ...(params.applyDateFilter && {
        startDate: formatDate(startDate, 'yyyy-MM-dd'),
        endDate: formatDate(endDate, 'yyyy-MM-dd'),
      }),
      accountNumber: undefined,
      policyNumber: params.policyNumber || undefined,
      fileNumber: params.fileNumber || undefined,
      orderNumber: params.orderNumber,
      invoiceNumber: invoiceNumber || undefined,
      isOpenOnly: false,
    };

    const oracleSearchPayload: InvoiceSearchRequestModel = {
      ...basePayload,
      firmId: params.firmId,
      isAdminDashboard: false,
    };

    // Make requests - conditionally include Agent Net search based on feature flag
    const requests: Promise<InvoiceSearchResult[]>[] = [
      getInvoicesApi(oracleSearchPayload, token).catch(() => []), // If user request fails, return empty array to allow admin results to show
    ];

    if (includeAgentNetData.enabled) {
      const agentNetSearchPayload: InvoiceSearchRequestModel = {
        ...basePayload,
        firmId: params.firmId,
        isAdminDashboard: true,
      };
      requests.push(getInvoicesApi(agentNetSearchPayload, token).catch(() => [])); // If admin request fails, return empty array to allow user results to show
    }

    const results = await Promise.all(requests);
    const oracleResults = results[0];
    const agentNetResults = includeAgentNetData.enabled ? results[1] : [];

    // Union join by invoiceNo - create a Map to deduplicate
    const resultsMap = new Map<string, InvoiceSearchResult>();

    // Add admin results first
    oracleResults.forEach((result) => {
      if (result.invoiceNo) {
        resultsMap.set(result.invoiceNo, result);
      }
    });

    // Add user results (only if invoiceNo doesn't already exist)
    agentNetResults.forEach((result) => {
      if (result.invoiceNo && !resultsMap.has(result.invoiceNo)) {
        resultsMap.set(result.invoiceNo, result);
      }
    });

    // Get merged results
    const mergedResults = Array.from(resultsMap.values());

    // Extract unique account numbers for batch lookup
    const accountNumbersWithDupes = mergedResults
      .map((r) => r.accountNo)
      .filter((acc): acc is string => acc != null && acc !== '')
      .map((acc) => parseInt(acc, 10))
      .filter((acc) => !isNaN(acc));
    const uniqueAccountNumbers = Array.from(new Set(accountNumbersWithDupes));

    // Batch lookup account info if there are account numbers
    if (uniqueAccountNumbers.length > 0) {
      try {
        const accountInfoList = await getAccountInfoApi({ accountNumbers: uniqueAccountNumbers }, token);
        const accountNameMap = new Map(accountInfoList.map((acc) => [acc.accountNumber.toString(), acc.businessName]));

        // Merge account names into results
        mergedResults.forEach((result) => {
          if (result.accountNo) {
            result.accountName = accountNameMap.get(result.accountNo) || undefined;
          }
        });
      } catch (error) {
        // If account lookup fails, continue without account names
        console.warn('Failed to fetch account info:', error);
      }
    }

    return mergedResults;
  };

  const {
    execute: executeSearchInvoices,
    status: searchStatus,
    value: searchResults,
  } = useAsync<InvoiceSearchResult[]>(searchInvoices, false);

  const handleDrawerOpen = async (invoice: InvoiceSearchResult) => {
    setInvoiceForDrawer(invoice);
    setOpenDrawer(true);
    setInvoiceDetailsLoading(true);
    setInvoiceDetailsData(undefined);
    setIsCplOnlyReopenEligible(false);

    try {
      const token = await getAccessToken();

      // Get invoice details
      const invoiceDetails = await getInvoiceDetailsApi(token, invoice.invoiceNo ?? '', Number(invoice.accountNo));

      // Check if invoice details were found
      if (!invoiceDetails?.fileList) {
        setInvoiceDetailsLoading(false);
        setMatchedInvoice(undefined);
        addGlobalMsg({
          message: 'Invoice details not found.',
          type: 'error',
        });
        return;
      }

      // Get underwriter from account info
      let underwriter = '';
      if (invoice.accountNo) {
        const accountInfoPayload = { accountNumbers: [Number(invoice.accountNo)] };
        const accountInfoList = await getAccountInfoApi(accountInfoPayload, token);
        if (accountInfoList && accountInfoList.length > 0) {
          underwriter = accountInfoList[0].underwriterCode || '';
        }
      }

      // Map new API response to OrdersDetailsModel format
      const fileList = invoiceDetails.fileList;
      const grossAmount = parseFloat(fileList.grossAmount) || 0;
      const netAmount = fileList.netAmount || 0;
      const amountDue = invoice.amountDue || 0;
      const adjustedNetAmount = grossAmount < 0 && netAmount > 0 ? -netAmount : netAmount;
      const credits = adjustedNetAmount - amountDue;
      const formattedInvoiceDate = invoice.invoiceDate ? invoice.invoiceDate.toString().split('T')[0] : '';

      const mappedInvoice: InvoiceModel = {
        invoiceNumber: invoice.invoiceNo || '',
        invoiceType: 'Remittance',
        invoiceDate: formattedInvoiceDate,
        fileOrderId: 0, // Not needed, set to 0
        fileId: fileList.fileId?.toString(),
        fileNumber: fileList.fileNo || '',
        underwriter: underwriter,
        jurisdictionStateCode: '',
        accountNumber: Number(invoice.accountNo || 0),
        property: {
          address1: fileList.propertyAddress?.address1 || '',
          address2: fileList.propertyAddress?.address2 || '',
          city: fileList.propertyAddress?.city || '',
          stateOrProvince: fileList.propertyAddress?.state || '',
          postalCode: fileList.propertyAddress?.zipCode || '',
          countyName: fileList.propertyAddress?.county || '',
        },
        gross: grossAmount,
        net: adjustedNetAmount,
        credits: credits,
        amountDue: amountDue,
        items:
          fileList.itemList?.map((item) => {
            const itemGross = parseFloat(item.grossAmount) || 0;
            const itemNet = item.netAmount || 0;
            // If gross is negative but net is positive, make net negative
            const adjustedNet = itemGross < 0 && itemNet > 0 ? -itemNet : itemNet;
            const namePieces = [];
            if (item.serialNo) {
              namePieces.push(item.serialNo);
            }
            if (item.description) {
              let cleanedDescription = item.description;
              if (item.serialNo && item.description.includes(item.serialNo)) {
                // Remove serial number and any trailing dash with spaces (e.g., "123 - " or "123-")
                const serialPattern = item.serialNo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                cleanedDescription = item.description.replace(new RegExp(`${serialPattern}\\s*-?\\s*`), '');
              }
              cleanedDescription = cleanedDescription.trim();
              if (cleanedDescription) {
                namePieces.push(cleanedDescription);
              }
            }
            const name = namePieces.join(' - ');
            return {
              type: item.lineItemType || '',
              name: name,
              gross: itemGross,
              net: adjustedNet,
            };
          }) || [],
      };

      // Encode numeric fileId to hashed string for URLs
      if (mappedInvoice.fileId && !isNaN(Number(mappedInvoice.fileId))) {
        mappedInvoice.fileId = HashUtils.encode(Number(mappedInvoice.fileId));
      }

      // Create OrdersDetailsModel structure
      const payInvoiceData: OrdersDetailsModel = {
        orders: [
          {
            orderNumber: invoice.orderNo?.toString() || '',
            orderStatus: '',
            underwriter: underwriter,
            orderDate: formattedInvoiceDate,
            isOrderInvoiced: true,
            isOrderAutoReport: false,
            invoices: [mappedInvoice],
          },
        ],
      };

      // Set the matched invoice for reuse in other functions
      setMatchedInvoice(mappedInvoice);

      // Set orderNumber and orderDate for reuse
      setOrderNumber(invoice.orderNo?.toString() || '');
      setOrderDate(formattedInvoiceDate);

      // Agent Net File - optional enhancement, don't fail if unavailable
      const fileId = mappedInvoice.fileId;
      if (fileId) {
        try {
          const file = await getFileInfo(fileId, token);

          // Verify file number matches before using the data
          const isFileNumberValid =
            mappedInvoice.fileNumber &&
            validateFileNumber({
              fileId,
              expectedFileNumber: mappedInvoice.fileNumber,
              actualFileNumber: file?.fileNumber,
            });

          if (isFileNumberValid) {
            setInvoiceFileData(file);

            // Check CPL-only reopen eligibility if file is Open
            if (file?.fileStatus === fileStatuses.open && file?.fileId) {
              try {
                setIsCheckingCplEligibility(true);
                const eligibility = await getCplOnlyRemittedFileEligibilityApi(token, HashUtils.decode(file.fileId));
                setIsCplOnlyReopenEligible(eligibility?.isReopenEligible ?? false);
              } catch (eligibilityError) {
                console.warn('Unable to check CPL eligibility:', eligibilityError);
                setIsCplOnlyReopenEligible(false);
              } finally {
                setIsCheckingCplEligibility(false);
              }
            }

            // Update matched invoice property with file data if available
            if (file?.properties && file.properties.length > 0) {
              const fileProperty =
                file.properties.find((prop) => prop.isDefaultProperty === true) || file.properties[0];
              mappedInvoice.property = {
                address1: fileProperty.address1 || '',
                address2: fileProperty.address2 || '',
                city: fileProperty.city || '',
                stateOrProvince: fileProperty.stateOrProvince || fileProperty.state || '',
                postalCode: fileProperty.postalCode || '',
                countyName: fileProperty.county || '',
              };

              // Update the payInvoiceData with the updated property info
              payInvoiceData.orders[0].invoices[0] = mappedInvoice;
            }
          }
        } catch (fileError) {
          // File info is supplementary - continue without it if unavailable
          console.warn('Unable to load file info for invoice:', fileId, fileError);
        }
      }

      setInvoiceDetailsData(payInvoiceData);
      setInvoiceDetailsLoading(false);
    } catch (error) {
      setInvoiceDetailsLoading(false);
      setMatchedInvoice(undefined);
      addGlobalMsg({
        message: 'Failed to load invoice details.',
        type: 'error',
      });
    }
  };

  const closeDrawer = () => {
    setOpenDrawer(false);
    setInvoiceDetailsData(undefined);
    setInvoiceFileData(undefined);
    setInvoiceForDrawer(undefined);
    setMatchedInvoice(undefined);
    setIsCplOnlyReopenEligible(false);
  };

  const createFileFromInvoiceData = async (invoice: InvoiceModel): Promise<void> => {
    if (!invoice) {
      throw new Error('Invoice data not available for file creation');
    }

    // Construct CreateInvoicedFileRequestModel from available invoice data
    const fileRequest: CreateInvoicedFileRequestModel = {
      accountNumber: invoice.accountNumber || 0,
      invoiceNumber: invoice.invoiceNumber || '',
      orderNumber: orderNumber,
      orderDate: orderDate,
    };

    const token = await getAccessToken();
    const response: CreateInvoicedFileResponseModel = await createInvoicedFileApi(fileRequest, token);

    // Update the invoice details data with the newly created fileId
    if (response?.fileId && invoiceDetailsData) {
      const updatedData = { ...invoiceDetailsData };
      // Since invoiceDetailsData has been filtered to only contain the matched invoice, it's at index 0
      if (updatedData.orders?.[0]?.invoices?.[0]) {
        updatedData.orders[0].invoices[0].fileId = response.fileId.toString();
      }
      setInvoiceDetailsData(updatedData);

      // Update the matched invoice state as well
      if (matchedInvoice) {
        setMatchedInvoice({ ...matchedInvoice, fileId: response.fileId });
      }
    }
  };

  const onFileReopen = async () => {
    if (isFileReopenInProgress || isCreatingFile || isCheckingFileExistence) return;

    const invoiceNumber = invoiceForDrawer?.invoiceNo;
    const accountNumber = matchedInvoice?.accountNumber;

    if (!accountNumber || isNaN(Number(accountNumber)) || !invoiceNumber) {
      addGlobalMsg({
        message: 'Account number or invoice number not available',
        type: 'error',
      });
      return;
    }

    if (!invoiceNumber) {
      addGlobalMsg({
        message: 'Invoice number not available',
        type: 'error',
      });
      return;
    }

    // CPL-only file with Open status - use credit memo flow directly
    if (isCplOnlyReopenEligible && invoiceFileData?.fileId && invoiceFileData?.fileStatus === fileStatuses.open) {
      try {
        setFileReopenInProgress(true);

        // Parse invoiceId from invoiceNo (e.g., "ABC-1234" -> 1234)
        const invoiceIdPieces = invoiceNumber.split('-');
        const invoiceIdStr = invoiceIdPieces[invoiceIdPieces.length - 1];
        const invoiceId = parseInt(invoiceIdStr, 10);

        if (isNaN(invoiceId)) {
          addGlobalMsg({
            message: 'Unable to parse invoice ID from invoice number',
            type: 'error',
          });
          setFileReopenInProgress(false);
          return;
        }

        const token = await getAccessToken();
        const response = await createCreditMemoApi(
          {
            fileId: HashUtils.decode(invoiceFileData.fileId),
            invoiceIds: [invoiceId],
            reason: 'reopen',
          },
          token,
        );

        setFileReopenInProgress(false);

        if (response?.creditMemoId) {
          addSnackbarMessage({
            message: 'CPL status updated and credit memo created successfully',
            type: 'success',
          });
          closeDrawer();
        } else {
          addGlobalMsg({
            message: response?.statusMessage || 'Failed to create credit memo',
            type: 'error',
          });
        }
      } catch (error: any) {
        setFileReopenInProgress(false);
        addGlobalMsg({
          message: error?.message || `Failed to process CPL reopen. ${AGENCY_SUPPORT_MESSAGE}`,
          type: 'error',
        });
      }
      return;
    }

    try {
      if (invoiceFileData) {
        // File exists, proceed directly to reopen modal
        setFileReopenModal(true);
        return;
      }

      // Check if file exists in Agent Net
      setIsCheckingFileExistence(true);
      const token = await getAccessToken();
      const invoiceDetails = await getInvoiceDetailsApi(token, invoiceNumber, accountNumber);
      setIsCheckingFileExistence(false);

      // If file doesn't exist, create it first
      if (!invoiceDetails?.fileList?.isAgentNetFile) {
        if (!matchedInvoice) {
          addGlobalMsg({
            message: 'Invoice data not found',
            type: 'error',
          });
          return;
        }

        setIsCreatingFile(true);

        try {
          await createFileFromInvoiceData(matchedInvoice);
          setIsCreatingFile(false);

          addSnackbarMessage({
            message: 'File created successfully',
            type: 'success',
          });

          // Proceed to open reopen modal
          setFileReopenModal(true);
        } catch (createError: any) {
          setIsCreatingFile(false);
          addGlobalMsg({
            message: createError?.message || `Failed to create file. ${AGENCY_SUPPORT_MESSAGE}`,
            type: 'error',
          });
          return; // Don't open reopen modal if file creation fails
        }
      } else {
        // File exists, proceed directly to reopen modal
        setFileReopenModal(true);
      }
    } catch (error: any) {
      setIsCheckingFileExistence(false);
      setIsCreatingFile(false);
      addGlobalMsg({
        message: `Failed to check file existence. ${AGENCY_SUPPORT_MESSAGE}`,
        type: 'error',
      });
    }
  };

  const onConfirmFileReopen = async (fileReopenObj: any, type: string) => {
    setFileReopenInProgress(true);

    if (type === 'endorsement') {
      const token = await getAccessToken();
      const payload = {
        fileId: invoiceFileData?.fileId,
        firmId: invoiceFileData?.firmId,
        officeId: invoiceFileData?.officeId,
        accountId: invoiceFileData?.accountId,
        status: fileStatuses.open,
        reopenByProduct: true,
        serviceStatus: {
          hasActiveCpl: invoiceFileData?.serviceStatus?.hasActiveCpl ?? false,
          hasActiveJacket: invoiceFileData?.serviceStatus?.hasActiveJacket ?? false,
        },
      };

      try {
        const saveFileStatusResponse = await saveFileStatus(payload, token);
      } catch (error: any) {
        setFileReopenInProgress(false);
        addGlobalMsg({
          message: `Failed to reopen file. ${error.data.messages.join(', ')}`,
          type: 'error',
        });
      }

      setFileReopenInProgress(false);
      setFileReopenModal(false);
      addSnackbarMessage({
        message: 'File reopened for endorsements successfully',
        type: 'success',
      });

      closeDrawer();
    } else {
      // Full reopen

      let fileId = matchedInvoice?.fileId;
      if (fileId && isNaN(Number(fileId))) {
        fileId = HashUtils.decode(fileId)?.toString() ?? fileId; // Decode fileId for API call, but keep original if decoding fails
      }

      const payload = {
        fileId,
        reason: fileReopenObj.reason,
      };

      try {
        const token = await getAccessToken();
        const response = await saveFileReopen(payload, token);
        setFileReopenInProgress(false);
        setFileReopenModal(false);

        if (response?.result) {
          addSnackbarMessage({
            message: 'File reopened successfully',
            type: 'success',
          });

          closeDrawer();
        } else if (response?.messages && response.messages.length > 0) {
          response.messages.forEach((msg: string) => {
            addGlobalMsg({
              message: msg,
              type: 'error',
            });
          });
        }
      } catch (error) {
        setFileReopenInProgress(false);
        addGlobalMsg({
          message: `Failed to reopen file. ${AGENCY_SUPPORT_MESSAGE}`,
          type: 'error',
        });
      }
    }
  };

  const today = new Date();
  const maxStartDate = endDate < today ? endDate : today;

  return (
    <>
      <ContentContainer fullWidth>
        <Content className={classes.root}>
          <div>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PageHeader
                  divider={useHeaderDivider !== undefined ? useHeaderDivider : true}
                  {...(showHeaderElipsis === undefined || showHeaderElipsis
                    ? {
                        menuItems: [
                          {
                            label: 'Knowledge Base',
                            link: KBLinks.home,
                          },
                        ],
                      }
                    : {})}
                  title={pageHeader || 'Invoices'}
                  contentRight={
                    <div style={{ display: 'flex' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            name="Advanced Search"
                            checked={advancedSearch}
                            onChange={() => {
                              setAdvancedSearch(!advancedSearch);
                            }}
                          />
                        }
                        labelPlacement="start"
                        label={'Advanced Search'}
                      />
                    </div>
                  }
                />
              </Grid>
            </Grid>
            {advancedSearch && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <AgentNetTextInput
                      fullWidth
                      data-testid={`${dataQaPrefix}-Advanced-Search-InvoiceNumber`}
                      label={'Invoice Number'}
                      value={invoiceNumber}
                      onChange={(event) => {
                        const value = event.target.value?.trim() ?? '';
                        setInvoiceNumber(value);
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <AgentNetDropdownSelector
                      name="dateFilter"
                      data-testid={`${dataQaPrefix}-Advanced-Search-Date-Filter`}
                      options={dateFilters ?? []}
                      value={dateFilter}
                      id="dateFilter"
                      label="Invoice Date Range"
                      dropdowntype="outlined"
                      menuOption={(value: string) => {
                        setDateFilter(value);
                        return false;
                      }}
                      prefixIcon={<CalendarIcon />}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <DateFieldString
                        label="Invoice Date - From"
                        format="MM/dd/yyyy"
                        value={formatDate(startDate, 'MM/dd/yyyy')}
                        disabled={dateFilter !== 'custom'}
                        onChange={(date) => {
                          if (date) {
                            const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
                            if (parsedDate <= endDate) {
                              setStartDate(parsedDate);
                            } else {
                              setStartDate(endDate);
                            }
                          }
                        }}
                        maxDate={formatDate(maxStartDate, 'MM/dd/yyyy')}
                      />
                    </MuiPickersUtilsProvider>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <DateFieldString
                        label="Invoice Date - To"
                        format="MM/dd/yyyy"
                        value={formatDate(endDate, 'MM/dd/yyyy')}
                        disabled={dateFilter !== 'custom'}
                        onChange={(date) => {
                          if (date) {
                            const parsedDate = parse(date, 'MM/dd/yyyy', new Date());
                            if (parsedDate >= startDate) {
                              setEndDate(parsedDate);
                            } else {
                              setEndDate(startDate);
                            }
                          }
                        }}
                        minDate={formatDate(startDate, 'MM/dd/yyyy')}
                        maxDate={formatDate(new Date(), 'MM/dd/yyyy')}
                      />
                    </MuiPickersUtilsProvider>
                  </Grid>
                  <AdvancedSearchTextFields
                    invoiceNumber={invoiceNumber}
                    onClearSearch={() => {
                      setDateFilter('today');
                      setStartDate(new Date());
                      setEndDate(new Date());
                      setInvoiceNumber('');
                      setSearchExecuted(false);
                    }}
                    onSearch={(params) => {
                      setSearchExecuted(true);
                      executeSearchInvoices(params);
                    }}
                  />
                </Grid>
              </>
            )}
            <div style={{ marginTop: 16 }} />
            {searchExecuted && (
              <InvoicesGrid
                invoices={searchResults || []}
                loading={searchStatus === 'pending'}
                selectedInvoices={selectedInvoices}
                onSelectionChanged={setSelectedInvoices}
                onInvoiceView={handleDrawerOpen}
              />
            )}
          </div>
        </Content>
      </ContentContainer>
      {openDrawer && invoiceForDrawer && (
        <FormDrawerComponent
          title={`Invoice No: ${invoiceForDrawer.invoiceNo}`}
          open={openDrawer}
          disableEnforceFocus
          crossQAPrefix="InvoiceProductDrawer"
          hidePrimaryAction
          actionItems={
            <>
              <AgentNetButton2
                color="primary"
                data-testid="OrderManagement-InvoiceProductDrawer-Export"
                onClick={() => {
                  // TODO: Implement export functionality if needed
                }}
                variant="outlined"
                size="small"
              >
                Export
              </AgentNetButton2>
              <Tooltip
                title={
                  isCplOnlyReopenEligible
                    ? 'Update CPL status and create credit memo'
                    : getReopenButtonTooltip({
                        fileStatus: invoiceFileData?.fileStatus,
                        grossAmount: matchedInvoice?.gross,
                        isCheckingFileExistence,
                        isCreatingFile,
                      })
                }
                arrow
                placement="top"
              >
                <span>
                  <AgentNetButton2
                    color="primary"
                    data-testid="OrderManagement-InvoiceProductDrawer-ReopenFile"
                    onClick={onFileReopen}
                    variant="contained"
                    size="small"
                    className={classes.reopenFile}
                    disabled={
                      isCheckingCplEligibility ||
                      (shouldDisableReopenButton({
                        fileStatus: invoiceFileData?.fileStatus,
                        grossAmount: matchedInvoice?.gross,
                        isCheckingFileExistence,
                        isCreatingFile,
                        invoiceDetailsLoading,
                      }) &&
                        !isCplOnlyReopenEligible)
                    }
                    loading={isCheckingFileExistence || isCreatingFile || isCheckingCplEligibility}
                  >
                    <HistoryOutlinedIcon />
                    {isCheckingFileExistence
                      ? 'Checking...'
                      : isCreatingFile
                      ? 'Creating File...'
                      : isCheckingCplEligibility
                      ? 'Checking...'
                      : 'Reopen File'}
                  </AgentNetButton2>
                </span>
              </Tooltip>
            </>
          }
          onDismissAction={closeDrawer}
          dismissActionLabel="Cancel"
          dismissActionProps={{
            'data-testid': 'OrderManagement-InvoiceProductDrawer-Cancel',
          }}
          fullWidth
        >
          {invoiceDetailsLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <LoadingSpinner status="pending" variant="circle" />
            </div>
          )}
          {!invoiceDetailsLoading && !matchedInvoice && (
            <div style={{ padding: '24px', textAlign: 'center', color: '#d32f2f' }}>
              Failed to load invoice details. {AGENCY_SUPPORT_MESSAGE}
            </div>
          )}
          {!invoiceDetailsLoading && matchedInvoice && (
            <InvoiceSummaryTable
              orderNumber={orderNumber}
              orderDate={orderDate}
              underwriter={matchedInvoice.underwriter}
              invoices={[
                {
                  invoiceNumber: matchedInvoice.invoiceNumber ?? '',
                  fileOrderId: matchedInvoice.fileOrderId,
                  fileId: Number(matchedInvoice.fileId ?? ''),
                  fileNumber: matchedInvoice.fileNumber ?? '',
                  underwriter: matchedInvoice.underwriter,
                  property: {
                    address1: matchedInvoice.property?.address1 ?? '',
                    address2: matchedInvoice.property?.address2 ?? '',
                    city: matchedInvoice.property?.city ?? '',
                    stateOrProvince: matchedInvoice.property?.stateOrProvince ?? '',
                    postalCode: matchedInvoice.property?.postalCode ?? '',
                  },
                  gross: matchedInvoice.gross,
                  net: matchedInvoice.net,
                  credits: matchedInvoice.credits,
                  amountDue: matchedInvoice.amountDue,
                  items: matchedInvoice.items.map(({ type, name, gross, net }: InvoiceItems) => ({
                    type,
                    name,
                    gross,
                    net,
                  })),
                },
              ]}
              isTitleRequired={false}
              showInvoiceNo={true}
            />
          )}
        </FormDrawerComponent>
      )}
      {hasRemitAccess && (
        <AgentNetConfirmFileReopenModal
          open={fileReopenModal}
          onClose={() => setFileReopenModal(false)}
          onConfirm={onConfirmFileReopen}
          isActionInProgress={isFileReopenInProgress}
          isReopenEligible={true}
          hasFSCRemittedProduct={false}
          hasRemitAccess={hasRemitAccess}
        />
      )}
    </>
  );
};

export default InvoicesPage;
